import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { estoqueApi } from '../../services/api';
import { NotaFiscal } from '../../types';
import { DocumentTextIcon, EyeIcon, CheckCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';

const NotaFiscalList: React.FC = () => {
  const navigate = useNavigate();
  const [notas, setNotas] = useState<NotaFiscal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadNotas();
  }, [statusFilter]);

  const loadNotas = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await estoqueApi.getNotasFiscais(params);
      setNotas(response.results || []);
    } catch (err) {
      setError('Erro ao carregar notas fiscais');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredNotas = notas.filter(nota =>
    nota.numero_nota.toLowerCase().includes(searchTerm.toLowerCase()) ||
    nota.emitente_razao_social?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    nota.chave_acesso?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (status: string, statusDisplay?: string) => {
    const statusColors: Record<string, string> = {
      'PENDENTE': 'bg-yellow-100 text-yellow-800',
      'CONFERIDA': 'bg-blue-100 text-blue-800',
      'LANCADA': 'bg-green-100 text-green-800',
      'CANCELADA': 'bg-gray-100 text-gray-800',
    };

    const displayText = statusDisplay || status;
    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
        {displayText}
      </span>
    );
  };

  const handleViewDetails = (id: string | number) => {
    navigate(`/estoque/notas-fiscais/${id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-gray-900">Notas Fiscais</h1>
          <p className="text-xs text-gray-600">Gerencie notas fiscais importadas</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Buscar por número, fornecedor ou chave..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todos os status</option>
            <option value="PENDENTE">Pendente Conferência</option>
            <option value="CONFERIDA">Conferida</option>
            <option value="LANCADA">Lançada no Estoque</option>
            <option value="CANCELADA">Cancelada</option>
          </select>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Data Emissão
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Número/Série
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fornecedor
              </th>
              <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Itens
              </th>
              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valor Total
              </th>
              <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {filteredNotas.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-2 py-4 text-center text-sm text-gray-500">
                  Nenhuma nota fiscal encontrada
                </td>
              </tr>
            ) : (
              filteredNotas.map((nota) => (
                <tr key={nota.id} className="hover:bg-gray-25">
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="text-xs text-gray-900">{formatDate(nota.data_emissao)}</div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="text-xs font-medium text-gray-900">
                      {nota.numero_nota}/{nota.serie}
                    </div>
                    <div className="text-xs text-gray-500 truncate max-w-xs" title={nota.chave_acesso}>
                      {nota.chave_acesso?.substring(0, 20)}...
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="text-xs text-gray-900">{nota.emitente_razao_social}</div>
                    {nota.emitente_cnpj && (
                      <div className="text-xs text-gray-500">{nota.emitente_cnpj}</div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center">
                    <div className="text-xs text-gray-900">
                      {nota.itens_mapeados || 0}/{nota.total_itens || 0}
                    </div>
                    <div className="text-xs text-gray-500">mapeados</div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-right">
                    <div className="text-xs font-semibold text-gray-900">
                      {formatCurrency(nota.valor_total)}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center">
                    {getStatusBadge(nota.status, nota.status_display)}
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-right">
                    <div className="flex justify-end space-x-1">
                      <button
                        onClick={() => handleViewDetails(nota.id)}
                        className="text-blue-600 hover:text-blue-900 p-0.5"
                        title="Ver detalhes"
                      >
                        <EyeIcon className="h-3.5 w-3.5" />
                      </button>
                      {nota.status === 'PENDENTE' && (
                        <button
                          onClick={() => navigate(`/estoque/notas-fiscais/${nota.id}/conferir`)}
                          className="text-green-600 hover:text-green-900 p-0.5"
                          title="Conferir"
                        >
                          <CheckCircleIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {nota.status === 'CONFERIDA' && (
                        <button
                          onClick={() => navigate(`/estoque/notas-fiscais/${nota.id}/lancar`)}
                          className="text-purple-600 hover:text-purple-900 p-0.5"
                          title="Lançar no Estoque"
                        >
                          <RocketLaunchIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="px-4 py-3 border-t border-gray-200 text-xs text-gray-600">
          Total: {filteredNotas.length} nota(s) fiscal(is)
        </div>
      </div>
    </div>
  );
};

export default NotaFiscalList;
